import {hasOwnProperty, hasString} from '~/utils/obj';
import type {EntryRef, EtherSeo, Image, SeoFallback, SeoInputRef} from '~/interfaces/seo';
import {getFbMetaFields, getTwMetaFields} from '~/utils/seo/social';
import {getRobots} from '~/utils/seo/robots';
import {getCanonical} from '~/utils/seo/canonical';
import {getSeoDescription} from '~/utils/seo/description';
import {getSeoImage} from '~/utils/seo/image';
import {fallbackImage} from '~/sitemap.config';

// Ether is returning encoded html values
const decodeHtmlChars = (str: string) => (!str || typeof str !== 'string') ? '' : str
    // Fix hex tags
    .replaceAll(/&#(\d+);/g, (_, dec) => String.fromCodePoint(Number(dec)))
    .replaceAll(/&#x([0-9a-fA-F]+);/g, (_, hex) => String.fromCodePoint(parseInt(hex, 16)))
    // Fix quotations
    .replaceAll(/&quot;/g, '"')
    // eslint-disable-next-line quotes
    .replaceAll(/&apos;/g, `'`)
    // Fix arrows
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    // Fix amps
    .replace(/&amp;/g, '&');
            
// Craft Ether Seo
export const useSeo = (
    seoInput: SeoInputRef,
    entry: EntryRef,
    fallbacks?: SeoFallback,
    activeSite?: any
) => computed(() => {
    const seoRef = initializeEtherSeoData(seoInput, entry, fallbacks, activeSite);
    const seo = toValue(seoRef);

    if (!seo) {
        return {};
    }

    const metaData = {
        meta: [
            ...getFbMetaFields(seo),
            ...getTwMetaFields(seo),
            // getRobots(seo)
        ],
    } as any;

    if (hasString(seo, 'title')) {
        const seoPrepend = toValue(activeSite)?.seo?.prepend;
        metaData.title = decodeHtmlChars(seoPrepend && !seo.title.includes(seoPrepend) ?
            `${seo.title} ${seoPrepend}` :
            seo.title
        );
    }

    if (hasString(seo, 'description')) {
        metaData.meta.push(
            { key: 'description', name: 'description', content: seo.description },
        );
    }

    if (getCanonical(seo)) {
        metaData.link = [
            getCanonical(seo)
        ];
    }

    const entryValue = toValue(entry);
    if (entryValue?.postDate) {
        metaData.meta.push(
            { property: 'article:published_time', content: entryValue.postDate },
        );
    }

    return metaData;
});


const initializeEtherSeoData = (
    refSeo: SeoInputRef,
    refEntry: EntryRef,
    fallbacks: SeoFallback = {},
    activeSite?: any
) => computed(() => {
    const seo: EtherSeo = toValue(refSeo) || {};
    const entry = toValue(refEntry);

    if (!entry && !fallbacks) {
        return seo;
    }

    // Set a default description based on introText or searchText if defined
    seo.description = seo.description || toValue(fallbacks.seoDescription) || getSeoDescription(entry) || '';

    // Set a default title based on entry title if not defined
    if (!hasString(seo, 'title')) {
        (seo as EtherSeo).title = entry?.title ?? fallbacks.seoTitle ?? '';
    }

    // Set a default image based on entry image, or fallback to static image instead
    if (!hasOwnProperty(seo, 'featuredimage')) {
        const availableImages = [
            entry?.overviewImage,
            entry?.headerImage
        ].filter(Boolean) as Image[][];

        const image = getSeoImage(availableImages);

        if (image) {
            // Set featured image from header- or overview image
            seo.featuredimage = { urlLarge: image };
        } else {
            // Set a generic image
            seo.featuredimage = {
                urlLarge: fallbacks.seoImage ?? toValue(activeSite)?.seo?.image ?? fallbackImage,
            };
        }
    }
    return seo;
});


